import { RightOutlined } from "@ant-design/icons"
import { Button } from "components/Button/Button"
import { Checkbox } from "components/Checkbox/Checkbox"
import { SearchInput } from "components/SearchInput/SearchInput"
import { Space } from "components/Space/Space"
import { Spinner } from "components/Spinner/Spinner"
import { useInfiniteScroll } from "hooks/useInfiniteScroll"
import { useListSort } from "hooks/useListSort"
import { Visit } from "interface/visit"
import { useRef } from "react"
import { Link, useSearchParams } from "react-router"
import { useVisits } from "services/visits"
import { dateFormatter } from "utils/date"

export function NeedsScheduling() {
	const targetScroll = useRef<HTMLUListElement>(null)
	const { sort, toggle, sortIcon, labelSort } = useListSort<Visit>(DEFAULT_SORT)
	const [searchParams, setSearchParams] = useSearchParams(DEFAULT_SEARCH_PARAMS)
	const query = searchParams.get("query") ?? ""
	const onlyUnreadMessages = searchParams.get("onlyUnreadMessages") === "true"
	const {
		data: { pages: visits = [] } = {},
		hasNextPage,
		fetchNextPage,
		isFetching,
		isFetchingNextPage,
	} = useVisits({ status: ["UNSCHEDULED"], sort, query, onlyUnreadMessages })
	const isLoading = isFetching || isFetchingNextPage

	useInfiniteScroll({
		target: targetScroll.current,
		hasMore: hasNextPage,
		isLoading,
		onLoadMore: fetchNextPage,
	})

	return (
		<Spinner spinning={isFetching}>
			<div className="@container mx-auto flex h-full max-h-full max-w-300 flex-col gap-3 overflow-hidden">
				<h2 className="font-nunito text-3xl font-semibold">Needs scheduling</h2>

				<div className="flex items-center gap-2">
					<SearchInput
						value={query}
						placeholder="Search by patient name, email, or phone number"
						onSearch={(query) => setSearchParams({ query })}
					/>
					<Checkbox
						checked={onlyUnreadMessages}
						className="min-w-fit"
						onChange={(e) =>
							setSearchParams({
								query,
								onlyUnreadMessages: String(e.target.checked),
							})
						}
					>
						Only unread messages
					</Checkbox>
				</div>

				<Space.Compact className="justify-end @3xl:hidden">
					<Button
						icon={sortIcon("createdAt")}
						onClick={() => toggle("createdAt")}
					>
						Request submitted
					</Button>
					<Button
						icon={sortIcon("updatedAt")}
						onClick={() => toggle("updatedAt")}
					>
						Last updated
					</Button>
					<Button
						icon={sortIcon("unreadMessagesCount")}
						onClick={() => toggle("unreadMessagesCount")}
					>
						Unread messages
					</Button>
				</Space.Compact>

				<div className="flex flex-col gap-4 overflow-hidden">
					<div className="hidden gap-4 rounded-lg bg-mint-0 p-5 py-3 @3xl:flex">
						<div className="grid w-full grid-cols-[repeat(4,1fr)_auto] items-center gap-4 font-bold">
							<div role="columnheader" className="whitespace-break-spaces">
								Visit type
							</div>
							<div
								role="columnheader"
								className="flex items-center gap-2 whitespace-break-spaces"
							>
								Request submitted
								<Button
									size="small"
									type="dashed"
									icon={sortIcon("createdAt")}
									aria-label={labelSort("createdAt", "request submitted")}
									onClick={() => toggle("createdAt")}
								/>
							</div>
							<div
								role="columnheader"
								className="flex items-center gap-2 whitespace-break-spaces"
							>
								Last updated
								<Button
									size="small"
									type="dashed"
									icon={sortIcon("updatedAt")}
									aria-label={labelSort("updatedAt", "last updated")}
									onClick={() => toggle("updatedAt")}
								/>
							</div>
							<div
								role="columnheader"
								className="flex items-center gap-2 whitespace-break-spaces"
							>
								Unread messages
							</div>
							<div className="invisible size-5" />
						</div>
					</div>

					<ul
						ref={targetScroll}
						className="flex flex-col gap-4 overflow-y-auto"
						aria-label={
							isLoading
								? "Waiting for unscheduled visits"
								: `${visits.length} unscheduled visit(s)`
						}
					>
						{visits.map(
							({
								createdAt,
								updatedAt,
								unreadMessagesCount,
								id,
								visitType,
								patient,
							}) => (
								<li key={id} aria-label={`Patient ${patient?.fullName}`}>
									<Link
										to={id}
										className="focus-outline flex w-full flex-col items-center gap-4 rounded-xl border border-mint-20 p-5 hover:border-green-60 @3xl:grid @3xl:grid-cols-[repeat(4,1fr)_auto]"
										aria-label={`Schedule patient ${patient?.fullName}`}
									>
										<div className="flex w-full justify-between gap-2 wrap-anywhere">
											<span className="text-navy-60 @3xl:hidden">
												Visit type:
											</span>
											<span className="text-navy-100 @3xl:text-navy-60">
												{visitType}
											</span>
										</div>

										<div className="flex w-full justify-between gap-2 wrap-anywhere">
											<span className="text-navy-60 @3xl:hidden">
												Request submitted:
											</span>
											<time className="text-navy-100 @3xl:text-navy-60">
												{dateFormatter(createdAt)}
											</time>
										</div>

										<div className="flex w-full justify-between gap-2 wrap-anywhere">
											<span className="text-navy-60 @3xl:hidden">
												Last updated:
											</span>
											<time className="text-navy-100 @3xl:text-navy-60">
												{dateFormatter(updatedAt)}
											</time>
										</div>

										<div className="flex w-full justify-between gap-2 wrap-anywhere">
											<span className="text-navy-60 @3xl:hidden">
												Unread messages:
											</span>
											<span className="text-blue-60">
												{unreadMessagesCount
													? `${unreadMessagesCount} unread messages`
													: ""}
											</span>
										</div>

										<div className="mx-5 my-0 w-full border border-dashed border-grey-20 @3xl:hidden" />

										<div className="flex gap-2 text-navy-60">
											<span className="@3xl:hidden">Details</span>
											<RightOutlined />
										</div>
									</Link>
								</li>
							),
						)}
					</ul>
				</div>
			</div>
		</Spinner>
	)
}

const DEFAULT_SEARCH_PARAMS = {
	query: "",
	onlyUnreadMessages: "false",
}

const DEFAULT_SORT = [
	"createdAt-ascending",
	"updatedAt-ascending",
	"unreadMessagesCount-ascending",
] as const
